/**
 * Site Configuration
 * 
 * This file centralizes all site-specific branding, contact information,
 * and feature toggles. When cloning this template for a new site,
 * update the values in this file to customize the site.
 * 
 * INSTRUCTIONS FOR NEW SITES:
 * 1. Update organizationName and tagline
 * 2. Update contact information (address, phone, email)
 * 3. Update social media links (or set enabled: false to hide)
 * 4. Configure feature flags (donation, membership, newsletter, etc.)
 * 5. Update branding colors in src/index.css if needed
 */

export type SocialPlatform = 'facebook' | 'instagram' | 'twitter' | 'youtube' | 'linkedin' | 'tiktok';

export type SocialLink = {
  platform: SocialPlatform;
  url: string;
  enabled: boolean;
};

export type SiteConfig = {
  // Organization Identity
  organizationName: string;
  tagline: string;
  description: string;
  foundedYear: number;
  logoUrl: string;
  
  // Contact Information
  contact: {
    address: {
      street: string;
      city: string;
      state: string;
      zip: string;
      country: string;
    };
    phone: string;
    email: string;
    hoursOfOperation?: string;
  };
  
  // Social Media Links
  socialLinks: SocialLink[];
  
  // Feature Flags - Toggle visibility of features
  features: {
    donation: {
      enabled: boolean;
      buttonText: string;
      pageTitle: string;
    };
    membership: {
      enabled: boolean;
      buttonText: string;
      pageTitle: string;
    };
    newsletter: {
      enabled: boolean;
      title: string;
      description: string;
    };
    events: {
      enabled: boolean;
      buttonText: string;
    };
    gallery: {
      enabled: boolean;
    };
    news: {
      enabled: boolean;
    };
    programs: {
      enabled: boolean;
    };
    testimonials: {
      enabled: boolean;
    };
    dailyFacts: {
      enabled: boolean;
      title: string;
    };
    sponsors: {
      enabled: boolean;
    };
    partners: {
      enabled: boolean;
    };
  };
  
  // Navigation Configuration
  navigation: {
    showEventsButton: boolean;
    showMembershipButton: boolean;
    showDonateButton: boolean;
  };
  
  // Footer Configuration
  footer: {
    showNewsletter: boolean;
    showSocialLinks: boolean;
    showQuickLinks: boolean;
    showPrograms: boolean;
    copyrightText?: string;
  };
  
  // Hero Section Configuration
  hero: {
    title: string;
    highlightedText: string;
    subtitle: string;
    showStats: boolean;
    stats: Array<{
      value: string;
      label: string;
    }>;
  };
  
  // Legacy CTA Section Configuration
  legacyCTA: {
    title: string;
    description: string;
  };
  
  // Welcome Section Configuration
  welcomeSection: {
    title: string;
    highlightedText: string;
    description: string;
    buttonText: string;
    buttonLink: string;
    enabled: boolean;
  };
  
  // SEO Defaults
  seo: {
    defaultTitle: string;
    titleTemplate: string;
    defaultDescription: string;
    defaultKeywords: string[];
  };
};

/**
 * Default site configuration
 * 
 * TEMPLATE USERS: Update these values for your organization
 * 
 * Quick Start:
 * 1. Replace "Your Organization Name" with your actual organization name
 * 2. Update contact information (address, phone, email)
 * 3. Update social media links or set enabled: false to hide
 * 4. Configure feature flags based on your needs
 * 5. Customize hero section text and stats
 */
export const siteConfig: SiteConfig = {
  // ===== ORGANIZATION IDENTITY =====
  // Replace these values with your organization's information
  organizationName: "FSHS Construction, Inc.", // Replace with your organization's name
  tagline: "Quality Construction Services in South Florida and Midwest Ohio", // Replace with your tagline
  description: "FSHS Construction, Inc. is a construction company in the South Florida and Midwest Ohio area. Founded in 2006, we offer a wide range of construction services.",
  foundedYear: 2006, // Replace with your organization's founding year
  logoUrl: "", // Organization logo URL
  
  // ===== CONTACT INFORMATION =====
  // Replace with your organization's contact details
  contact: {
    address: {
      street: "1997 Hamrock Dr.", // Replace with your street address
      city: "Powell", // Replace with your city
      state: "OH", // Replace with your state/province
      zip: "43065", // Replace with your ZIP/postal code
      country: "USA", // Replace with your country
    },
    phone: "614-378-4262", // Replace with your phone number
    email: "shimjaewon@yahoo.com", // Replace with your email address
    hoursOfOperation: "Monday - Friday: 8AM - 5PM", // Replace with your hours (optional)
  },
  
  // ===== SOCIAL MEDIA LINKS =====
  socialLinks: [
    { platform: 'facebook', url: 'https://facebook.com', enabled: true },
    { platform: 'instagram', url: 'https://instagram.com', enabled: true },
    { platform: 'twitter', url: 'https://twitter.com', enabled: true },
    { platform: 'youtube', url: 'https://youtube.com', enabled: true },
    { platform: 'linkedin', url: 'https://linkedin.com', enabled: false },
    { platform: 'tiktok', url: 'https://tiktok.com', enabled: false },
  ],
  
  // ===== FEATURE FLAGS =====
  features: {
    donation: {
      enabled: false, // Set to true to enable donation-related UI (organization-focused feature)
      buttonText: "Donate",
      pageTitle: "Support Our Mission",
    },
    membership: {
      enabled: false, // Set to true to enable membership-related UI (organization-focused feature)
      buttonText: "Join",
      pageTitle: "Become a Member",
    },
    newsletter: {
      enabled: true,
      title: "Stay Connected",
      description: "Get updates on events, programs, and community news.",
    },
    events: {
      enabled: true,
      buttonText: "Events",
    },
    gallery: {
      enabled: true,
    },
    news: {
      enabled: true,
    },
    programs: {
      enabled: true,
    },
    testimonials: {
      enabled: true,
    },
    dailyFacts: {
      enabled: true,
      title: "Daily Fact",
    },
    sponsors: {
      enabled: true,
    },
    partners: {
      enabled: true,
    },
  },
  
  // ===== NAVIGATION CONFIGURATION =====
  navigation: {
    showEventsButton: true,
    showMembershipButton: false, // Only shows if features.membership.enabled is also true
    showDonateButton: false,     // Only shows if features.donation.enabled is also true
  },
  
  // ===== FOOTER CONFIGURATION =====
  footer: {
    showNewsletter: true,
    showSocialLinks: true,
    showQuickLinks: true,
    showPrograms: true,
    copyrightText: undefined, // Uses default if undefined
  },
  
  // ===== HERO SECTION =====
  // Customize the homepage hero section
  hero: {
    title: "Welcome to", // First part of hero title
    highlightedText: "FSHS Construction, Inc.", // Highlighted part of hero title
    subtitle: "Quality construction services in South Florida and Midwest Ohio. Licensed, bonded, and insured. We see your project through from inception to completion, controlling time and cost, and delivering quality work.",
    showStats: true, // Set to false to hide statistics
    stats: [
      { value: "2006", label: "Founded" }, // Replace with your actual statistics
      { value: "2", label: "Office Locations" },
      { value: "4", label: "Service Areas" },
    ],
  },
  
  // ===== LEGACY CTA SECTION =====
  // Final call-to-action section at bottom of homepage
  legacyCTA: {
    title: "Be Part of Our Legacy",
    description: "Whether you're interested in joining our community, attending events, or supporting our mission, there's a place for you here.",
  },
  
  // ===== WELCOME SECTION =====
  // Welcome section below hero on homepage
  welcomeSection: {
    title: "Welcome to",
    highlightedText: "Our Website",
    description: "FSHS Construction, Inc. is a construction company in the South Florida and Midwest Ohio area. Founded in 2006, we offer a wide range of construction services. You can also see your project from the beginning to the end.",
    buttonText: "Learn More About Us",
    buttonLink: "/about-us",
    enabled: true,
  },
  
  // ===== SEO DEFAULTS =====
  // Search engine optimization defaults
  seo: {
    defaultTitle: "FSHS Construction, Inc. - Quality Construction Services", // Replace with your organization name
    titleTemplate: "%s | FSHS Construction, Inc.", // Page title template
    defaultDescription: "FSHS Construction, Inc. provides quality construction services in South Florida and Midwest Ohio. Licensed, bonded, and insured. Specializing in commercial, residential, and hurricane rehabilitation projects.",
    defaultKeywords: ["construction", "general contractor", "South Florida", "Ohio", "commercial construction", "residential construction", "hurricane rehab", "mold remediation"], // Add relevant keywords for SEO
  },
};

// ===== HELPER FUNCTIONS =====

/**
 * Get the full formatted address
 */
export const getFullAddress = (): string => {
  const { street, city, state, zip } = siteConfig.contact.address;
  return `${street}, ${city}, ${state} ${zip}`;
};

/**
 * Get enabled social links only
 */
export const getEnabledSocialLinks = (links: SocialLink[] = siteConfig.socialLinks): SocialLink[] => {
  return links.filter(link => link.enabled);
};

/**
 * Check if a feature is enabled
 */
export const isFeatureEnabled = (feature: keyof SiteConfig['features']): boolean => {
  return siteConfig.features[feature].enabled;
};

/**
 * Get navigation items based on enabled features
 * Note: This is kept for backward compatibility and fallback scenarios.
 * The navigation system now primarily uses the dynamic useNavigationItems hook
 * which pulls navigation items directly from the database.
 *
 * @deprecated Use useNavigationItems hook for dynamic CMS-based navigation
 */
export const getNavigationItems = (features: SiteConfig["features"] = siteConfig.features) => {
  console.warn('getNavigationItems is deprecated. Use useNavigationItems hook for dynamic navigation.');
  
  const items = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about-us" },
    { name: "Services", path: "/services" },
  ];
  
  if (features.programs.enabled) {
    items.push({ name: "Programs", path: "/programs" });
  }
  
  if (features.news.enabled) {
    items.push({ name: "News", path: "/news" });
  }
  
  if (features.gallery.enabled) {
    items.push({ name: "Gallery", path: "/gallery" });
  }
  
  // Membership/Join is only shown as a button (CTA), not as a menu item
  // The button is conditionally rendered in navigation components based on features.membership.enabled
  
  items.push({ name: "Contact", path: "/contact" });
  
  return items;
};

/**
 * Fallback navigation items for when database is unavailable
 * Used by useNavigationItems hook as backup
 */
export const getFallbackNavigationItems = () => {
  return [
    { name: 'Home', path: '/', order: 0 },
    { name: 'About', path: '/about-us', order: 10 },
    { name: 'Services', path: '/services', order: 20 },
    { name: 'Contact', path: '/contact', order: 70 }
  ];
};

export default siteConfig;
